/**
 * Persists conference pay-later flow data (registration + event) in sessionStorage
 * so users can move between Registration Success and Payment without losing state.
 * Session is cleared when the user navigates outside these two routes.
 */

const STORAGE_KEY = "tripsecure_conference_paylater_session";
const MAX_AGE_MS = 30 * 60 * 1000; // 30 minutes

/** Paths that belong to the pay-later flow (payment + registration-success for an event) */
const PAY_LATER_FLOW_REGEX = /^\/event-details\/[^/]+\/(?:registration-success|payment)$/;

/**
 * @param {string} pathname - current location.pathname
 * @returns {boolean} true if path is part of the pay-later flow
 */
export function isPayLaterFlowPath(pathname) {
  return PAY_LATER_FLOW_REGEX.test(pathname);
}

/**
 * Clear session when user leaves the pay-later flow (e.g. goes to /conference-registrations or /event-details/:slug).
 * Call this on route changes when pathname is NOT a pay-later flow path.
 */
export function clearPayLaterSession() {
  try {
    sessionStorage.removeItem(STORAGE_KEY);
  } catch (e) {
    // ignore
  }
}

/**
 * @param {string} slug - event slug
 * @param {object} payload - { registrationData, eventData, isGroup?, numberOfAttendees?, totalAmount?, attendees?, selectedTicket? }
 */
export function savePayLaterSession(slug, payload) {
  if (!slug) return;
  try {
    const data = {
      slug,
      registrationData: payload.registrationData ?? null,
      eventData: payload.eventData ?? null,
      isGroup: payload.isGroup ?? false,
      numberOfAttendees: payload.numberOfAttendees ?? undefined,
      totalAmount: payload.totalAmount ?? undefined,
      attendees: payload.attendees ?? undefined,
      selectedTicket: payload.selectedTicket ?? undefined,
      savedAt: Date.now(),
    };
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(data));
  } catch (e) {
    // ignore
  }
}

/**
 * @returns {object|null} { slug, registrationData, eventData, ... } or null if missing/expired
 */
export function loadPayLaterSession() {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const data = JSON.parse(raw);
    if (!data?.slug) return null;
    if (data.savedAt && Date.now() - data.savedAt > MAX_AGE_MS) {
      clearPayLaterSession();
      return null;
    }
    return data;
  } catch (e) {
    return null;
  }
}

/**
 * Load session only if it matches the given event slug (e.g. user opened payment for same event).
 * @param {string} slug - event slug
 * @returns {object|null} session payload or null
 */
export function loadPayLaterSessionForSlug(slug) {
  const data = loadPayLaterSession();
  if (!data || data.slug !== slug) return null;
  return data;
}
